import { Dropdown } from 'antd'
import dayjs from 'dayjs'
import moment from 'moment'
import settings from 'popups/Settings'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import * as CalendarService from '../../../modules/CalendarService'
import styles from './SchedulingAssistantTab.module.scss'
import SchedulingDayBox from './SchedulingDayBox'

import chevronDown24SVG from 'assets/images/chevron-down-24.svg'
import navigateTodaySVG from 'assets/images/icon-today-calendar.svg'
import navigateNextSVG from 'assets/images/navigation-next-24.svg'
import navigatePrevSVG from 'assets/images/navigation-prev-24.svg'
import durationSVG from 'assets/images/toolbar-duration.svg'
import CalendarDatePicker from 'pages/todo/components/DatePicker'
import { DatePickerType } from 'pages/todo/components/DatePicker/DatePickerType'
import DurationDropdown from './components/DurationDropdown'
import type { Attendee } from './types'
import type { BusyTimeData } from './utils/conflictDetection'

interface TimeSelection {
  start: Date
  end: Date
  allday: boolean
}

interface AttendeeBusyTimes {
  [email: string]: BusyTimeData
}

interface DomainSeparatedEmails {
  sameDomain: string[]
  differentDomain: string[]
}

interface SchedulingAssistantTabProps {
  attendees: Attendee[]
  currentDate: Date
  initialSelections?: TimeSelection[]
  onSelectionsChange: (selections: TimeSelection[]) => void
  activeTab: string
  eventTitle?: string
  multiMode?: boolean
  onAttendeeBusyTimesChange?: (busyTimes: AttendeeBusyTimes) => void
  onOwnerConflictChange?: (hasConflict: boolean) => void
}

const SchedulingAssistantTab: React.FC<SchedulingAssistantTabProps> = ({
  attendees,
  currentDate,
  initialSelections = [],
  onSelectionsChange,
  activeTab,
  eventTitle,
  multiMode = false,
  onAttendeeBusyTimesChange,
  onOwnerConflictChange,
}) => {
  // ========================================================================
  // 상태 관리
  // ========================================================================
  const [browseDate, setBrowseDate] = useState<Date>(currentDate || new Date())
  const [preserveScroll, setPreserveScroll] = useState<boolean>(false)
  const [attendeeBusyTimes, setAttendeeBusyTimes] = useState<AttendeeBusyTimes>({})
  const [error, setError] = useState<string | null>(null)
  const [datePickerOpen, setDatePickerOpen] = useState<boolean>(false)
  const [durationPickerOpen, setDurationPickerOpen] = useState<boolean>(false)

  // Unified state: always use array internally
  const [selections, setSelections] = useState<TimeSelection[]>(initialSelections)

  // User-selected duration (persists until component unmounts)
  // null = use default from settings or first selection
  const [userSelectedDuration, setUserSelectedDuration] = useState<number | null>(null)

  // Track first entry to scheduling tab (for scroll behavior in edit mode)
  const [hasEnteredSchedulingTab, setHasEnteredSchedulingTab] = useState<boolean>(false)

  /**
   * Sync selections when initialSelections prop changes
   */
  useEffect(() => {
    setSelections(initialSelections)
  }, [initialSelections])

  /**
   * 새로 진입 시 userSelectedDuration 초기화
   * activeTab이 'scheduling'으로 변경될 때마다 settings의 defaultDuration으로 초기화
   */
  useEffect(() => {
    if (activeTab === 'scheduling') {
      setUserSelectedDuration(null)
      setPreserveScroll(false)

      // 일정도우미 탭 재진입 시 currentDate(이벤트 시작일)로 browseDate 동기화
      if (currentDate) {
        setBrowseDate(currentDate)
      }

      // Mark that we've entered scheduling tab (for scroll behavior)
      if (!hasEnteredSchedulingTab) {
        setHasEnteredSchedulingTab(true)
      }
    }
  }, [activeTab, hasEnteredSchedulingTab])

  /**
   * currentDate prop 변경 시 browseDate 동기화
   * Event 탭에서 시작 날짜가 실제로 변경된 경우에만 browseDate 업데이트
   * 객체 참조가 아닌 날짜 값(timestamp)을 의존성으로 사용하여
   * 불필요한 effect 실행 및 browseDate 원복을 방지
   */
  const currentDateTimestamp = currentDate?.getTime()
  useEffect(() => {
    if (
      currentDate &&
      dayjs(currentDate).format('YYYY-MM-DD') !== dayjs(browseDate).format('YYYY-MM-DD')
    ) {
      setBrowseDate(currentDate)
    }
    // browseDate를 의존성에 포함하면 무한루프 발생.
  }, [currentDateTimestamp])

  /**
   * 참석자 이메일 목록 메모이제이션
   *
   * 배열 참조 변경이 아닌 실제 이메일 목록 내용이 변경될 때만 업데이트
   */
  const attendeeEmails = useMemo(() => {
    return attendees
      .map((a) => a.email)
      .sort()
      .join(',')
  }, [attendees])

  /**
   * 참석자 이메일을 도메인별로 분리
   *
   * @param emailList - 이메일 목록
   * @param userDomain - 사용자 도메인
   * @returns 도메인별 이메일 목록
   */
  const separateEmailsByDomain = (
    emailList: string[],
    userDomain: string,
  ): DomainSeparatedEmails => {
    const sameDomain: string[] = []
    const differentDomain: string[] = []

    emailList.forEach((email) => {
      const emailDomain = email.split('@')[1]
      if (emailDomain === userDomain) {
        sameDomain.push(email)
      } else {
        differentDomain.push(email)
      }
    })

    return { sameDomain, differentDomain }
  }

  /**
   * selectedDateRange - 선택된 시간 범위의 날짜 범위 추출 (시간 제외, 날짜만)
   *
   * 여러 selection이 있는 경우 가장 이른 시작 날짜부터 가장 늦은 종료 날짜까지 반환
   * 이 범위를 사용하여 conflict 확인을 위한 attendee busy time을 조회
   */
  const selectedDateRange = useMemo(() => {
    if (!selections || selections.length === 0) return null

    // Find earliest start and latest end
    let earliestStart = dayjs(selections[0].start).startOf('day')
    let latestEnd = dayjs(selections[0].end).startOf('day')

    selections.forEach((range) => {
      const start = dayjs(range.start).startOf('day')
      const end = dayjs(range.end).startOf('day')
      if (start.isBefore(earliestStart)) earliestStart = start
      if (end.isAfter(latestEnd)) latestEnd = end
    })

    return {
      startDateStr: earliestStart.format('YYYY-MM-DD'),
      endDateStr: latestEnd.format('YYYY-MM-DD'),
      start: earliestStart.toDate(),
      end: latestEnd.toDate(),
    }
  }, [selections])

  /**
   * API 조회에 사용할 날짜 범위 계산
   *
   * 1. selection이 있는 경우: browseDate와 selection 범위를 합친 확장된 범위 조회
   *    - browseDate의 busy status 표시 + selection의 conflict 확인 모두 필요
   *    - 예: browseDate=11/15, selection=11/20 → 11/15~11/20 전체 조회
   * 2. selection이 없는 경우: browseDate 기준 하루 전체를 조회
   */
  const queryDateRange = useMemo(() => {
    const navDayStart = dayjs(browseDate).startOf('day')
    const navDayEnd = dayjs(browseDate).endOf('day')

    if (selectedDateRange) {
      // Selection이 있으면 browseDate와 selection 범위를 합친 확장된 범위 사용
      const selectionStart = dayjs(selectedDateRange.start).startOf('day')
      const selectionEnd = dayjs(selectedDateRange.end).endOf('day')

      // 두 범위 중 더 이른 시작일과 더 늦은 종료일 선택 (합집합)
      const startDate = navDayStart.isBefore(selectionStart)
        ? navDayStart.toDate()
        : selectionStart.toDate()
      const endDate = navDayEnd.isAfter(selectionEnd) ? navDayEnd.toDate() : selectionEnd.toDate()

      return { startDate, endDate }
    } else {
      // Selection이 없으면 browseDate 기준 하루만
      return {
        startDate: navDayStart.toDate(),
        endDate: navDayEnd.toDate(),
      }
    }
  }, [selectedDateRange, browseDate])

  /**
   * 쿼리 날짜 범위 키 (useEffect 의존성 배열용)
   *
   * queryDateRange가 변경될 때만 재조회하도록 안정화
   * browseDate가 변경되면 selection 여부와 관계없이 재조회 (browseDateStr 포함)
   */
  const queryDateRangeKey = useMemo(() => {
    const browseDateStr = dayjs(browseDate).format('YYYY-MM-DD')
    return `${browseDateStr}|${queryDateRange.startDate.getTime()}|${queryDateRange.endDate.getTime()}`
  }, [queryDateRange, browseDate])

  /**
   * 참석자 바쁜 시간 데이터 조회
   *
   * 다음 경우에 실행됨:
   * 1. 탭이 'scheduling'으로 활성화될 때
   * 2. 날짜(browseDate)가 변경될 때 (일 단위 네비게이션)
   * 3. 참석자 목록(attendees)이 변경될 때
   * 4. selection의 날짜 범위가 변경될 때
   *
   * 조회 범위:
   * - selection이 있는 경우: browseDate + selection 범위의 합집합 (확장된 범위)
   *   - browseDate의 busy status 표시와 selection의 conflict 확인 모두 가능
   * - selection이 없는 경우: browseDate 기준 하루 전체
   * - browseDate 변경 시 selection 여부와 관계없이 재조회
   */
  useEffect(() => {
    const fetchFreeBusyData = async () => {
      // 탭이 활성화되지 않았거나 참석자가 없으면 조회하지 않음
      if (activeTab !== 'scheduling' || !attendeeEmails) {
        return
      }

      setError(null)

      try {
        // 사용자 정보에서 도메인 추출
        const userInfo = CalendarService.getUserInfo()
        const userDomain = userInfo?.email?.split('@')[1]
        const userEmail = userInfo?.email?.toLowerCase()

        if (!userDomain) {
          throw new Error('Unable to determine user domain')
        }

        // 이메일 목록을 배열로 변환 및 소유자/조직자 제외
        // 소유자의 이벤트는 ownerEvents에서 이미 처리되므로 attendeeBusyTimes에서는 제외
        const emailList = attendeeEmails
          .split(',')
          .filter((email) => email.toLowerCase() !== userEmail)

        // 도메인별 분리
        const { sameDomain, differentDomain } = separateEmailsByDomain(emailList, userDomain)

        // 조회 대상이 없으면(본인만 있는 경우 등) setAttendeeBusyTimes 를 호출하지 않는다.
        // 매번 새 빈 객체로 부모 state 를 갱신하면 부모 re-render 가 cascading 되어
        // EmailForm 의 initialSelections (.map 결과) → setSelections → re-render 무한 루프를
        // 유발한다.
        if (sameDomain.length === 0 && differentDomain.length === 0) {
          return
        }

        const busyData = {}

        // 같은 도메인 참석자의 바쁜 시간 데이터만 조회
        if (sameDomain.length > 0) {
          const sameDomainBusyData = await CalendarService.fetchFreeBusyInfo(
            sameDomain,
            queryDateRange.startDate,
            queryDateRange.endDate,
          )
          Object.assign(busyData, sameDomainBusyData)
        }

        // 다른 도메인 참석자는 알 수 없음 상태로 설정
        differentDomain.forEach((email) => {
          busyData[email] = {
            busy: [],
            tentative: [],
            outOfOffice: [],
            noData: false,
            unknown: true,
          }
        })

        // 새로운 데이터로 완전히 교체
        // 날짜 변경 시 이전 데이터는 더 이상 필요 없음
        setAttendeeBusyTimes(busyData)

        // 외부로 attendeeBusyTimes 전달
        if (onAttendeeBusyTimesChange) {
          onAttendeeBusyTimesChange(busyData)
        }
      } catch (err) {
        console.error('Failed to fetch free/busy data:', err)
        setError(err instanceof Error ? err.message : 'Failed to load availability data')
      }
    }

    fetchFreeBusyData()
  }, [activeTab, queryDateRangeKey, attendeeEmails, onAttendeeBusyTimesChange])

  // ========================================================================
  // 이벤트 핸들러
  // ========================================================================

  /**
   * 날짜 네비게이션 핸들러
   */
  const handlePrevDay = () => {
    const newDate = dayjs(browseDate).subtract(1, 'day').toDate()
    setPreserveScroll(true)
    setBrowseDate(newDate)
  }

  const handleNextDay = () => {
    const newDate = dayjs(browseDate).add(1, 'day').toDate()
    setPreserveScroll(true)
    setBrowseDate(newDate)
  }

  const handleToday = () => {
    setPreserveScroll(false)
    setBrowseDate(new Date())
  }

  /**
   * Duration change handler
   *
   * Updates all selections (multi-mode) or single selection (single-mode)
   * by adjusting end times while preserving start times
   * Also stores the user-selected duration to persist across operations
   *
   * @param durationMinutes - New duration in minutes
   */
  const handleChangeDuration = (durationMinutes: number) => {
    // Store user's explicit duration selection
    setUserSelectedDuration(durationMinutes)

    if (selections.length === 0) {
      setDurationPickerOpen(false)
      return
    }

    const updatedSelections = selections.map((selection) => {
      const newEnd = new Date(selection.start.getTime() + durationMinutes * 60 * 1000)
      return {
        ...selection,
        end: newEnd,
      }
    })

    setSelections(updatedSelections)

    if (onSelectionsChange) {
      onSelectionsChange(updatedSelections)
    }

    setDurationPickerOpen(false)
  }

  /**
   * 날짜 선택 핸들러
   */
  const handleDateSelect = (date: Date) => {
    setBrowseDate(date)
    setDatePickerOpen(false)
  }

  const handleDatePickerClose = () => {
    setDatePickerOpen(false)
  }

  /**
   * 소유자 충돌 상태 변경 핸들러
   */
  const handleOwnerConflictChange = useCallback(
    (hasConflict) => {
      // setOwnerHasConflict(hasConflict)
      if (onOwnerConflictChange) {
        onOwnerConflictChange(hasConflict)
      }
    },
    [onOwnerConflictChange],
  )

  /**
   * 현재 선택된 duration 계산 (분 단위)
   *
   * DurationDropdown에서 표시할 duration을 결정합니다.
   * 사용자가 dropdown에서 명시적으로 선택한 경우에만 그 값을 사용하고,
   * 그렇지 않으면 settings의 defaultDuration을 사용합니다.
   * 드래그로 만든 selection의 duration은 dropdown 표시에 영향을 주지 않습니다.
   *
   * Priority:
   * 1. User explicitly selected duration (via dropdown)
   * 2. Settings default duration
   */
  const currentDuration = useMemo(() => {
    // Priority 1: User explicitly selected duration
    if (userSelectedDuration !== null) {
      return userSelectedDuration
    }

    // Priority 2: Settings default duration
    // 드래그로 만든 selection이 있어도 dropdown에서는 settings의 defaultDuration을 표시
    return settings.getValue('calendar').defaultDuration
  }, [userSelectedDuration])

  /**
   * 시간 선택 핸들러
   *
   * SchedulingDayBox에서 시간 선택 시 호출됨
   * Multi-mode의 경우 removal도 처리 (startDate, endDate가 null이고 index가 제공됨)
   * 드래그로 만든 selection은 duration 설정을 따르지 않고 드래그한 영역 그대로 사용
   *
   * @param startDate - 시작 시간 (removal의 경우 null)
   * @param endDate - 종료 시간 (removal의 경우 null)
   * @param allday - All-day 여부
   * @param index - Multi-mode removal을 위한 index
   */
  const handleTimeSelect = (
    startDate: Date | null,
    endDate: Date | null,
    allday: boolean,
    index?: number,
  ) => {
    // Multi-mode: Handle removal if both dates are null and index is provided
    if (multiMode && startDate === null && endDate === null && index !== undefined) {
      const updatedSelections = selections.filter((_, i) => i !== index)
      setSelections(updatedSelections)

      if (onSelectionsChange) {
        onSelectionsChange(updatedSelections)
      }
      return
    }

    // 시간 범위 유효성 검증
    if (!startDate || !endDate) {
      console.error('Invalid time selection: missing dates')
      return
    }

    if (startDate >= endDate) {
      console.error('Invalid time selection: start must be before end')
      return
    }

    // 드래그로 만든 selection은 duration 설정을 따르지 않고 드래그한 영역 그대로 사용
    const newSelection = { start: startDate, end: endDate, allday }

    if (multiMode) {
      // Multi-mode: update existing selection or add new one
      let updatedSelections

      if (index !== undefined && index >= 0 && index < selections.length) {
        // Update existing selection at index (사용자가 드래그로 크기 조정한 경우)
        updatedSelections = selections.map((sel, i) => (i === index ? newSelection : sel))
      } else {
        // Add new selection (드래그로 만든 영역 그대로 사용)
        updatedSelections = [...selections, newSelection]
      }

      setSelections(updatedSelections)

      if (onSelectionsChange) {
        onSelectionsChange(updatedSelections)
      }
    } else {
      // Single-mode: replace array with single item
      const updatedSelections = [newSelection]

      // Single-mode에서 resize/move가 완료된 경우 (기존 selection이 업데이트되는 경우)
      // resize된 duration을 userSelectedDuration에 저장하여 이후 click 시 유지되도록 함
      if (selections.length > 0 && selections[0]) {
        const durationMinutes = Math.round((endDate.getTime() - startDate.getTime()) / (60 * 1000))
        setUserSelectedDuration(durationMinutes)
      }

      setSelections(updatedSelections)

      if (onSelectionsChange) {
        onSelectionsChange(updatedSelections)
      }
    }
  }

  /**
   * 날짜 표시 문자열 생성
   */
  const dateDisplayStr = useMemo(() => {
    const dateStr = moment(browseDate).startOf('day')
    return dateStr.format('ll')
  }, [browseDate])

  const scrollTargetTime = useMemo(() => {
    if (multiMode) return null
    if (selections.length > 0) return selections[0].start
    return new Date(currentDate)
  }, [multiMode, selections, currentDate])

  return (
    <div
      className={
        multiMode ? styles.selectAvailabilityContainer : styles.schedulingAssistantTabContainer
      }
      style={multiMode ? { height: '100%' } : {}}
    >
      <div className={styles.toolbar}>
        <div className={styles.navigationGroup}>
          <button className={styles.navigationButton} onClick={handlePrevDay}>
            <img src={navigatePrevSVG} alt="prev" />
          </button>

          <button className={styles.navigationButton} onClick={handleToday}>
            <img src={navigateTodaySVG} alt="today" />
          </button>

          <button className={styles.navigationButton} onClick={handleNextDay}>
            <img src={navigateNextSVG} alt="next" />
          </button>

          <Dropdown
            open={datePickerOpen}
            onOpenChange={setDatePickerOpen}
            destroyOnHidden={true}
            popupRender={() => {
              return (
                <div className={styles.datePickerContainer}>
                  <CalendarDatePicker
                    pickerType={DatePickerType.DATE}
                    value={browseDate}
                    useTodayMenu={false}
                    useDialogStyle={true}
                    onChange={handleDateSelect}
                    onClose={handleDatePickerClose}
                  />
                </div>
              )
            }}
            trigger={['click']}
            placement="bottomLeft"
          >
            <button className={styles.dateButton}>
              {dateDisplayStr}
              <img src={chevronDown24SVG} alt="chevron-down" style={{ marginLeft: '8px' }} />
            </button>
          </Dropdown>

          {multiMode && (
            <div style={{ position: 'absolute', right: 0, paddingRight: 10 }}>
              <Dropdown
                open={durationPickerOpen}
                onOpenChange={setDurationPickerOpen}
                destroyOnHidden={true}
                popupRender={() => (
                  <DurationDropdown
                    onDurationChange={handleChangeDuration}
                    currentDuration={currentDuration}
                  />
                )}
                trigger={['click']}
                placement="bottomRight"
              >
                <button className={styles.navigationButton}>
                  <img src={durationSVG} alt="duration" />
                </button>
              </Dropdown>
            </div>
          )}
        </div>
      </div>
      <div className={styles.content}>
        <div className={styles.dayView}>
          <SchedulingDayBox
            currentDate={browseDate}
            hideTodos={true}
            attendeeBusyTimes={attendeeBusyTimes}
            onTimeSelect={handleTimeSelect}
            attendeesCount={attendees.length}
            eventTitle={eventTitle}
            selections={selections}
            multiMode={multiMode}
            currentDuration={currentDuration}
            onOwnerConflictChange={handleOwnerConflictChange}
            scrollTargetTime={scrollTargetTime}
            activeTab={activeTab}
            preserveScroll={preserveScroll}
          />
          {error && (
            <div className={styles.loadingOverlay}>
              <div className={styles.errorContent}>
                <div>{error}</div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default SchedulingAssistantTab
